/**
 * Estabelecimentos Page - Gerenciamento de Estabelecimentos
 */

import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { EstabelecimentosTable } from "@/components/estabelecimento/EstabelecimentosTable";
import { EstabelecimentoFormDialog } from "@/components/estabelecimento/EstabelecimentoFormDialog";
import { EstabelecimentoViewDialog } from "@/components/estabelecimento/EstabelecimentoViewDialog";
import { EstabelecimentoDeleteDialog } from "@/components/estabelecimento/EstabelecimentoDeleteDialog";
import { Estabelecimento } from "@/types/estabelecimento";
import { EstabelecimentoFormData } from "@/schemas/estabelecimentoSchema";
import { estabelecimentosAdapter } from "@/data";
import { useToast } from "@/hooks/use-toast";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

export default function Estabelecimentos() {
  const [estabelecimentos, setEstabelecimentos] = useState<Estabelecimento[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedEstabelecimento, setSelectedEstabelecimento] = useState<Estabelecimento | null>(null);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [actionLoading, setActionLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadEstabelecimentos();
  }, []);

  const loadEstabelecimentos = async () => {
    try {
      setLoading(true);
      const response = await estabelecimentosAdapter.list();
      setEstabelecimentos(response.items);
    } catch (error) {
      console.error("Error loading estabelecimentos:", error);
      setEstabelecimentos([]); // Garante UI consistente
      
      // Toast APENAS para erros REAIS (RLS, network, etc.)
      if (error && typeof error === 'object') {
        const errorMessage = (error as any)?.message || '';
        const errorCode = (error as any)?.code || '';
        
        // Ignora "banco vazio" ou resultados vazios normais
        if (
          !errorMessage.toLowerCase().includes('no rows') &&
          !errorMessage.toLowerCase().includes('empty') &&
          errorCode !== 'PGRST116' // Supabase: no rows
        ) {
          toast({
            title: "Erro ao carregar estabelecimentos",
            description: errorMessage || "Não foi possível carregar a lista de estabelecimentos.",
            variant: "destructive",
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormMode("create");
    setSelectedEstabelecimento(null);
    setFormOpen(true);
  };

  const handleEdit = (estabelecimento: Estabelecimento) => {
    setFormMode("edit");
    setSelectedEstabelecimento(estabelecimento);
    setFormOpen(true);
  };

  const handleView = (estabelecimento: Estabelecimento) => {
    setSelectedEstabelecimento(estabelecimento);
    setViewOpen(true);
  };

  const handleDelete = (estabelecimento: Estabelecimento) => {
    setSelectedEstabelecimento(estabelecimento);
    setDeleteOpen(true);
  };

  const handleFormSubmit = async (data: EstabelecimentoFormData) => {
    try {
      setActionLoading(true);

      if (formMode === "create") {
        await estabelecimentosAdapter.create(data);
        toast({
          title: "Estabelecimento criado",
          description: "O estabelecimento foi criado com sucesso.",
        });
      } else if (selectedEstabelecimento) {
        await estabelecimentosAdapter.update(selectedEstabelecimento.id_estabelecimento, data);
        toast({
          title: "Estabelecimento atualizado",
          description: "O estabelecimento foi atualizado com sucesso.",
        });
      }

      loadEstabelecimentos();
      setFormOpen(false);
    } catch (error) {
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar o estabelecimento.",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedEstabelecimento) return;

    try {
      setActionLoading(true);
      console.log("🗑️ Deletando estabelecimento:", selectedEstabelecimento);
      await estabelecimentosAdapter.delete(selectedEstabelecimento.id_estabelecimento);
      
      toast({
        title: "✅ Estabelecimento excluído",
        description: "O estabelecimento foi excluído com sucesso.",
      });

      loadEstabelecimentos();
      setDeleteOpen(false);
    } catch (error: any) {
      console.error("❌ Erro ao deletar estabelecimento:", error);
      
      let errorMessage = "Não foi possível excluir o estabelecimento.";
      
      if (error?.message?.includes("vendas ou entradas associadas")) {
        errorMessage = error.message;
      } else if (error?.message?.includes("policy") || error?.code === "42501") {
        errorMessage = "Você não tem permissão para excluir estabelecimentos.";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro ao excluir",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Estabelecimentos</h1>
          <p className="text-muted-foreground">
            Gerencie os estabelecimentos do sistema
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" text="Carregando estabelecimentos..." />
          </div>
        ) : (
          <EstabelecimentosTable
            estabelecimentos={estabelecimentos}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onCreate={handleCreate}
          />
        )}
      </div>

      <EstabelecimentoFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        mode={formMode}
        estabelecimento={selectedEstabelecimento}
        onSubmit={handleFormSubmit}
        isLoading={actionLoading}
      />

      <EstabelecimentoViewDialog
        open={viewOpen}
        onOpenChange={setViewOpen}
        estabelecimento={selectedEstabelecimento}
      />

      <EstabelecimentoDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        estabelecimento={selectedEstabelecimento}
        onConfirm={handleConfirmDelete}
        isLoading={actionLoading}
      />
    </Layout>
  );
}
